<section class="py-8 md:py-12 lg:py-16">
  <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
    <!-- Post-payment return: resolve the pending order, or show a fallback -->
    <div *ngIf="isConfirmationMode" class="max-w-2xl mx-auto text-center">
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-8">
        <h1 class="text-2xl font-bold text-gray-900 mb-2">
          Processing your top-up
        </h1>
        <p class="text-gray-600 mb-6">
          We couldn't automatically match this payment to your session. If
          you completed payment, your ticket wallet balance will update
          shortly. You can check your tickets and orders for the latest
          status.
        </p>
        <a
          routerLink="/"
          class="inline-block bg-black hover:bg-gray-800 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
        >
          Return to Home
        </a>
      </div>
    </div>

    <!-- Top-up form -->
    <div *ngIf="!isConfirmationMode" class="max-w-2xl mx-auto">
      <header class="text-center mb-8">
        <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Top Up Ticket Wallet
        </h1>
        <p class="text-gray-600">
          Add funds to your ticket's wallet using our secure payment gateway.
        </p>
      </header>

      <form
        [formGroup]="form"
        (ngSubmit)="submit()"
        class="bg-white rounded-lg border border-gray-200 shadow-sm p-6 md:p-8 space-y-5"
      >
        <!--
          The ticket is fixed by the uid this page was opened with, so it is shown rather than
          entered. It used to be an editable field, which meant the customer could type a number
          that resolves to a different ticket — or, on an event whose numbers repeat across ticket
          types, to no single ticket at all. Top-up now always starts from My Tickets.
        -->
        <div>
          <span class="block text-sm font-medium text-gray-700 mb-1">Ticket</span>
          <p class="text-gray-900 font-medium">
            {{ ticketNumberLabel ? '#' + ticketNumberLabel : 'Selected ticket' }}
          </p>
          <a
            routerLink="/my-tickets"
            class="text-xs text-gray-600 underline mt-1 inline-block"
          >
            Top up a different ticket
          </a>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label for="amount" class="block text-sm font-medium text-gray-700 mb-1">
              Amount
            </label>
            <input
              id="amount"
              type="number"
              min="0.01"
              step="0.01"
              formControlName="amount"
              placeholder="0.00"
              class="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
            />
            <p
              *ngIf="form.get('amount')?.invalid && form.get('amount')?.dirty"
              class="text-xs text-red-600 mt-1"
            >
              Enter an amount greater than zero.
            </p>
          </div>

          <!--
            Currency is the ticket type's, not a choice. Letting the customer pick meant they could
            fund a wallet in a currency the ticket does not trade in, leaving a balance the booths
            cannot spend against.
          -->
          <div>
            <span class="block text-sm font-medium text-gray-700 mb-1">Currency</span>
            <p class="py-2.5 text-gray-900 font-medium">
              {{ currencyCode || '—' }}
            </p>
            <p class="text-xs text-gray-500">Set by your ticket type.</p>
          </div>
        </div>

        <div>
          <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            formControlName="email"
            placeholder="you@example.com"
            class="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
          />
          <p
            *ngIf="form.get('email')?.invalid && form.get('email')?.dirty"
            class="text-xs text-red-600 mt-1"
          >
            A valid email address is required.
          </p>
        </div>

        <div>
          <label for="contactNumber" class="block text-sm font-medium text-gray-700 mb-1">
            Contact Number <span class="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="contactNumber"
            type="tel"
            formControlName="contactNumber"
            placeholder="e.g. +263 7X XXX XXXX"
            class="block w-full border border-gray-300 rounded-md py-2.5 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
          />
        </div>

        <div class="pt-2">
          <button
            type="submit"
            [disabled]="submitting || resolving || !currencyCode"
            class="w-full bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center transition-colors duration-200"
          >
            <span *ngIf="!submitting">Top Up Wallet</span>
            <span *ngIf="submitting" class="flex items-center">
              <img src="assets/loading.gif" alt="Loading" class="w-5 h-5 mr-2" />
              Processing...
            </span>
          </button>
          <p class="text-xs text-gray-500 text-center mt-3">
            You will be redirected to our secure payment gateway
          </p>
        </div>
      </form>
    </div>
  </div>
</section>
